import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import NotificationsRoundedIcon from "@mui/icons-material/NotificationsRounded";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import WorkspacePremiumRoundedIcon from "@mui/icons-material/WorkspacePremiumRounded";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import authService from "../../services/auth";
import { useAlerts } from "../../context/AlertsContext";
import DeviceSwitcher from "../DeviceSwitcher/DeviceSwitcher";
import ThemeToggle from "../ThemeToggle/ThemeToggle";
import "./TopBar.css";

const titles = {
  "/home": { title: "Dashboard", subtitle: "Monitoreo de tu consumo de agua en tiempo real" },
  "/devices": { title: "Dispositivos", subtitle: "Administra los equipos vinculados a tu cuenta" },
  "/devices/link": { title: "Vincular dispositivo", subtitle: "Añade un equipo nuevo a tu cuenta" },
  "/subscription": { title: "Planes", subtitle: "Tu suscripción y lo que incluye" },
  "/simulated": { title: "Dashboard", subtitle: "Explora métricas y gráficas de ejemplo" },
  "/history": { title: "Historial", subtitle: "Consumo, costos y desglose por hora" },
  "/alerts": { title: "Alertas", subtitle: "Eventos detectados en tu instalación" },
  "/valve-control": { title: "Control de Válvula", subtitle: "Apertura, cierre y estado en tiempo real" },
  "/recommendations": { title: "Recomendaciones", subtitle: "Sugerencias personalizadas para ahorrar" },
  "/profile": { title: "Perfil", subtitle: "Configuración y reportes de la cuenta" },
};

export default function TopBar({ onMenuClick }) {
  const location = useLocation();
  const navigate = useNavigate();
  const meta = titles[location.pathname] || {
    title: "NisaGuard",
    subtitle: "Sistema inteligente de monitoreo de agua",
  };

  const [userInfo, setUserInfo] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const triggerRef = useRef(null);
  const { totalUnreadAlerts } = useAlerts();

  useEffect(() => {
    setUserInfo(authService.getUserInfo());
  }, []);

  // El menú se cierra al pulsar fuera o con Escape: sin esto queda abierto
  // flotando sobre la pantalla siguiente al navegar con el teclado.
  useEffect(() => {
    if (!menuOpen) return undefined;

    const onPointerDown = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };
    const onKeyDown = (event) => {
      if (event.key !== "Escape") return;
      setMenuOpen(false);
      // El foco vuelve al disparador: si no, cerrar con Escape lo deja en la
      // nada y el siguiente Tab empieza desde el principio de la página.
      triggerRef.current?.focus();
    };

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [menuOpen]);

  useEffect(() => setMenuOpen(false), [location.pathname]);

  const userName = userInfo?.name || userInfo?.email?.split("@")[0] || "Usuario";
  const userEmail = userInfo?.email || "";
  const initials = userName
    .split(" ")
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const handleLogout = () => {
    authService.logout();
    navigate("/login");
  };

  return (
    <header className="ng-topbar">
      <div className="ng-topbar__left">
        <button
          type="button"
          className="ng-btn ng-btn--ghost ng-btn--icon ng-topbar__menu"
          onClick={onMenuClick}
          aria-label="Abrir menú"
        >
          <MenuRoundedIcon />
        </button>
        <div className="ng-topbar__titles">
          <h1 className="ng-topbar__title">{meta.title}</h1>
          <p className="ng-topbar__subtitle">{meta.subtitle}</p>
        </div>
      </div>

      <div className="ng-topbar__right">
        {/* Ámbito activo: el usuario debe saber siempre si mira toda la cuenta
            o un solo dispositivo, sin tener que deducirlo del contenido. */}
        <DeviceSwitcher />

        <ThemeToggle className="ng-topbar__theme" />

        <Link
          to="/alerts"
          className="ng-btn ng-btn--ghost ng-btn--icon ng-topbar__bell"
          aria-label={
            totalUnreadAlerts > 0
              ? `Alertas, ${totalUnreadAlerts} sin leer`
              : "Alertas"
          }
        >
          <NotificationsRoundedIcon />
          {totalUnreadAlerts > 0 && (
            <span className="ng-topbar__bell-count">
              {totalUnreadAlerts > 9 ? "9+" : totalUnreadAlerts}
            </span>
          )}
        </Link>

        <div className="ng-topbar__account" ref={menuRef}>
          <button
            ref={triggerRef}
            type="button"
            className={`ng-topbar__user ${menuOpen ? "is-open" : ""}`}
            onClick={() => setMenuOpen((v) => !v)}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
          >
            <span className="ng-topbar__avatar" aria-hidden="true">
              {initials || <PersonRoundedIcon fontSize="small" />}
            </span>
            <span className="ng-topbar__user-name">{userName}</span>
            <KeyboardArrowDownRoundedIcon className="ng-topbar__caret" />
          </button>

          {menuOpen && (
            <div className="ng-menu ng-topbar__account-menu" role="menu">
              <div className="ng-topbar__menu-id">
                <span className="ng-topbar__menu-name">{userName}</span>
                {userEmail && (
                  <span className="ng-topbar__menu-email">{userEmail}</span>
                )}
              </div>
              <span className="ng-menu__sep" role="presentation" />
              <Link to="/profile" className="ng-menu__item" role="menuitem">
                <PersonRoundedIcon />
                Perfil y ajustes
              </Link>
              <Link to="/subscription" className="ng-menu__item" role="menuitem">
                <WorkspacePremiumRoundedIcon />
                Planes
              </Link>
              <span className="ng-menu__sep" role="presentation" />
              <div className="ng-topbar__menu-theme">
                <span className="ng-menu__label">Tema</span>
                <ThemeToggle />
              </div>
              <span className="ng-menu__sep" role="presentation" />
              <button
                type="button"
                className="ng-menu__item"
                role="menuitem"
                onClick={handleLogout}
              >
                <LogoutRoundedIcon />
                Cerrar sesión
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
